import { getStores } from "@/api/masterAdmin/MainAdminApi.js";
import { getDepartments } from "@/api/store/DepartmentsService.js";
import { getSavedState, saveState } from "@/helpers/LocalStorage.js";
import { getStoreSellers } from "@/api/store/SellersService.js";
import {
  getModalidadVenta as getModalidadVentaApi,
  getPriceLevels as getPriceLevelsApi
} from "@/api/store/Infoauto.js";
import { loadGoogleAdsCategories } from "@/api/store/GoogleAdsService.js";
import { loadPriceGroups as loadPriceGroupsApi } from "@/api/store/PriceGroupsService.js";
import {
  getSettingsSection,
  getShippingServices
} from "@/api/store/SettingsService";
import { getWarehouses } from "@/api/store/WarehouseService.js";

// Requests for a department's warehouses already on their way: several
// components ask at once (GeneralInfo on every order reload)
const warehouseRequests = new Map();
console.log("getSavedState", getSavedState("store.selectedDepartment"));
export const state = {
  appVersion: require("../../../package.json").version,
  showMenu: false,
  // El asistente abierto a todo el alto (AssistantBubble): la página y los
  // modales le dejan sitio a la derecha
  assistantDocked: false,
  stores: [],
  departments: [],
  activeStore: null,
  activeDepartment: null,
  mainLoading: true,
  sellers: [],
  googleAdsCategories: [],
  priceGroups: [],
  priceLevels: [],
  adminSettings: new Map(),
  modalidadesVenta: [],
  shippingServices: [],
  options: new Map(),
  // Warehouses (depositos) of each department of the active store, by departamentoId
  warehousesByDepartment: {}
};

export const getters = {
  getDepartmentWarehouses: state => departamentoId =>
    state.warehousesByDepartment[departamentoId] || [],
  getAppVersion(state) {
    return state.appVersion;
  },
  getStatusMenu(state) {
    return state.showMenu;
  },
  getAssistantDocked(state) {
    return state.assistantDocked;
  },
  getMainLoading(state) {
    return state.mainLoading;
  },
  getStores(state) {
    return state.stores;
  },
  getDepartments(state) {
    return state.departments;
  },
  getActiveStore(state) {
    return state.activeStore;
  },
  getSellers(state) {
    return state.sellers;
  },
  getGoogleAdsCategories(state) {
    return state.googleAdsCategories;
  },
  getPriceGroups(state) {
    return state.priceGroups;
  },
  getPriceLevels(state) {
    return state.priceLevels;
  },
  getAdminSettings(state) {
    return state.adminSettings;
  },
  getModalidadesVenta(state) {
    return state.modalidadesVenta;
  },
  getShippingServices(state) {
    return state.shippingServices;
  },
  getStoreOptions(state) {
    return state.options;
  },
  getActiveDepartment(state) {
    return state.activeDepartment;
  }
};

export const mutations = {
  UPDATE_MENU(state, newStatus) {
    state.showMenu = newStatus;
  },
  UPDATE_ASSISTANT_DOCKED(state, docked) {
    state.assistantDocked = docked;
  },
  UPDATE_STORES(state, stores) {
    state.stores = stores;
  },
  UPDATE_DEPARTMENTS(state, departments) {
    state.departments = departments;
  },
  UPDATE_SELECTED_STORE(state, storeData) {
    state.activeStore = storeData;
  },
  UPDATE_MAIN_LOADING(state, newStatus) {
    state.mainLoading = newStatus;
  },
  UPDATE_SELLERS(state, sellers) {
    state.sellers = sellers;
  },
  UPDATE_PRICE_GROUPS(state, priceGroups) {
    state.priceGroups = priceGroups;
  },
  UPDATE_PRICE_LEVELS(state, priceLevels) {
    state.priceLevels = priceLevels;
  },
  UPDATE_INFOAUTO_SETTINGS(state, settings) {
    state.adminSettings = settings;
  },
  // Refleja en adminSettings un setting recién guardado. Se reemplaza el Map
  // entero porque Vue 2 no detecta Map.set().
  UPDATE_ADMIN_SETTING(state, { key, value, status }) {
    const settings = new Map(state.adminSettings);
    settings.set(key, { ...settings.get(key), key, value, status });
    state.adminSettings = settings;
  },
  STORE_OPTIONS(state, options) {
    state.options = options;
  },
  UPDATE_GOOGLE_ADS_CATEGORIES(state, googleAdsCategories) {
    state.googleAdsCategories = googleAdsCategories;
  },
  UPDATE_MODALIDAD_VENTA(state, modalidadesVenta) {
    state.modalidadesVenta = modalidadesVenta;
  },
  UPDATE_SHIPPING_SERVICES(state, shippingServices) {
    state.shippingServices = shippingServices;
  },
  UPDATE_SELECTED_DEPARTMENT(state, department) {
    state.activeDepartment = department;
  },
  UPDATE_DEPARTMENT_WAREHOUSES(state, { departamentoId, warehouses }) {
    state.warehousesByDepartment = {
      ...state.warehousesByDepartment,
      [departamentoId]: warehouses
    };
  },
  // Each store (country) has its own warehouses
  RESET_DEPARTMENT_WAREHOUSES(state) {
    state.warehousesByDepartment = {};
  }
};

export const actions = {
  // Loaded once per store and department; later calls reuse them
  loadDepartmentWarehouses({ state, commit }, departamentoId) {
    if (!departamentoId) return Promise.resolve([]);
    if (state.warehousesByDepartment[departamentoId]) {
      return Promise.resolve(state.warehousesByDepartment[departamentoId]);
    }
    if (!warehouseRequests.has(departamentoId)) {
      const request = getWarehouses(departamentoId)
        .then(warehouses => {
          // getWarehouses answers [] on error: not cached, asked again next time
          if (warehouses && warehouses.length) {
            commit("UPDATE_DEPARTMENT_WAREHOUSES", {
              departamentoId,
              warehouses
            });
          }
          return warehouses || [];
        })
        .finally(() => warehouseRequests.delete(departamentoId));
      warehouseRequests.set(departamentoId, request);
    }
    return warehouseRequests.get(departamentoId);
  },
  async init({ commit, dispatch, rootGetters }, { store, departmentCode }) {
    if (rootGetters["auth/currentUser"]) {
      let stores = await getStores();
      commit("UPDATE_STORES", stores);
      dispatch("setStore", {
        newStore: store,
        departmentCode: departmentCode
      });
    }
    commit("UPDATE_MAIN_LOADING", false);
  },
  getShippingServices({ commit }) {
    getShippingServices().then(shippingServices => {
      commit("UPDATE_SHIPPING_SERVICES", shippingServices);
    });
  },
  updatePriceLevelsInfoauto({ commit }) {
    getPriceLevelsApi().then(res => {
      commit("UPDATE_PRICE_LEVELS", res);
    });
  },
  getPriceGroups({ commit }) {
    loadPriceGroupsApi().then(res => {
      commit("UPDATE_PRICE_GROUPS", res);
    });
  },
  getGoogleAdsCategories({ commit }) {
    loadGoogleAdsCategories().then(res => {
      commit("UPDATE_GOOGLE_ADS_CATEGORIES", res);
    });
  },
  getModalidadVenta({ commit }) {
    getModalidadVentaApi().then(res => {
      commit("UPDATE_MODALIDAD_VENTA", res);
    });
  },
  updateShowMenu({ commit }, newStatus) {
    commit("UPDATE_MENU", newStatus);
  },
  updateAssistantDocked({ commit }, docked) {
    commit("UPDATE_ASSISTANT_DOCKED", docked);
  },
  async setStore(
    { getters, rootGetters, commit, dispatch },
    { newStore, departmentCode }
  ) {
    if (!newStore) {
      newStore = getSavedState("store.selectedStore");
    }
    newStore = newStore || rootGetters["auth/currentUser"]?.defaultStore;
    let stores = getters.getStores;
    let newStoreData = stores?.find(s => s.code == newStore);
    if (newStoreData?.code) {
      // Los departamentos son de cada tienda: hasta que setDepartment elija
      // el nuevo, las páginas que se montan con la tienda nueva (Store.vue
      // tiene :key) no deben pedir con el departamento de la anterior.
      const previousStoreCode = getters.getActiveStore?.code;
      if (previousStoreCode && previousStoreCode !== newStoreData.code) {
        saveState("store.selectedDepartment", "all");
        commit("UPDATE_SELECTED_DEPARTMENT", {
          code: "all",
          name: "All Departments"
        });
      }
      saveState("store.selectedStore", newStoreData.code);
      saveState("store.selectedStoreUrl", newStoreData.endpoint);
      commit("UPDATE_SELECTED_STORE", newStoreData);
      warehouseRequests.clear();
      commit("RESET_DEPARTMENT_WAREHOUSES");
      getSettingsSection().then(settings => {
        const settingsMap = new Map();
        settings.forEach(setting => {
          settingsMap.set(setting.key, setting);
        });
        commit("UPDATE_INFOAUTO_SETTINGS", settingsMap);
        saveState("store.adminSettings", settings);
      });
      getSettingsSection("options").then(settings => {
        const settingsMap = new Map();
        settings.forEach(setting => {
          settingsMap.set(setting.key, setting);
        });

        commit("STORE_OPTIONS", settingsMap);
      });
    }
    let departments = await getDepartments();
    let newDepartmentCode =
      departmentCode ||
      rootGetters["auth/currentUser"]?.defaultDepartmentCode ||
      null;
    const newDepartment = departments.find(d => d.code === newDepartmentCode);
    commit("UPDATE_DEPARTMENTS", departments);
    dispatch("setDepartment", newDepartment);
    return true;
  },
  async setDepartment({ getters, commit, dispatch }, newDepartment) {
    if (!newDepartment) {
      newDepartment = getSavedState("store.selectedDepartment");
    }
    if (!newDepartment || newDepartment.code == "all") {
      newDepartment = { code: "all", name: "All Departments" };
      saveState("store.selectedDepartment", newDepartment.code);
      commit("UPDATE_SELECTED_DEPARTMENT", newDepartment);
    } else {
      let departments = getters.getDepartments;
      let newDepartmentData = departments?.find(
        d => d.code == newDepartment.code
      );
      if (newDepartmentData?.code) {
        saveState("store.selectedDepartment", newDepartmentData.code);
        commit("UPDATE_SELECTED_DEPARTMENT", newDepartmentData);
      } else {
        // department not found, set to all
        newDepartment = { code: "all", name: "All Departments" };
        saveState("store.selectedDepartment", newDepartment.code);
        commit("UPDATE_SELECTED_DEPARTMENT", newDepartment);
      }
    }
    getStoreSellers().then(sellers => {
      commit("UPDATE_SELLERS", sellers);
    });
    dispatch("updatePriceLevelsInfoauto");
    dispatch("getPriceGroups");
    dispatch("getModalidadVenta");
    dispatch("getGoogleAdsCategories");
    dispatch("getShippingServices");
  }
};
